import { useEffect, useState } from "react";
import { Badge, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import {
  FaCertificate,
  FaCheckCircle,
  FaClipboardList,
  FaExclamationTriangle,
  FaHandsHelping,
  FaIndustry,
  FaRoute,
} from "react-icons/fa";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

type ProductLine = {
  id_product_line: number;
  product_name: string;
};

type MarketSegment = {
  id_segment: number;
  segment_name: string;
};

type AdhesionCertification = {
  id_adhesion_certification: number;
  certification_name: string;
};

type OemContact = {
  id_oem_contact: number;
  constructor_name?: string | null;
  contact_name?: string | null;
  phone?: string | null;
  duration_months?: string | null;
  status_level?: string | null;
};

type Blocker = {
  id_blocker: number;
  blocker_text: string;
  status: string;
  severity: string;
};

type SupportNeed = {
  id_support_need: number;
  support_need_text: string;
  status: string;
  priority: string;
};

type AdhesionData = {
  id_adhesion: number;
  account_role: string;
  company_type?: string | null;
  company_name: string;
  site_name?: string | null;
  wilaya?: string | null;
  factory_address?: string | null;
  factory_location?: string | null;
  products_count?: string | null;
  factory_capacity?: string | null;
  contact_name?: string | null;
  phone?: string | null;
  email?: string | null;
  product_homologated?: string | null;
  contacted_by_oem?: string | null;
  respondent_name?: string | null;
  response_date?: string | null;
  signature?: string | null;
  status: string;
  created_at: string;
  products: ProductLine[];
  market_segments: MarketSegment[];
  certifications: AdhesionCertification[];
  oem_contacts: OemContact[];
  blockers: Blocker[];
  support_needs: SupportNeed[];
};

type Props = {
  userId?: number | string | null;
};

const valueOrDash = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const InfoCard = ({
  label,
  value,
  icon,
}: {
  label: string;
  value?: string | number | null;
  icon: React.ReactNode;
}) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body className="d-flex gap-3 align-items-start">
      <span className="aida-adhesion-icon">{icon}</span>
      <div>
        <div className="text-muted small fw-semibold">{label}</div>
        <div className="fw-bold text-dark">{valueOrDash(value)}</div>
      </div>
    </Card.Body>
  </Card>
);

export default function AidaAdhesionTab({ userId }: Props) {
  const [adhesion, setAdhesion] = useState<AdhesionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadAdhesion = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await fetch(`${backendUrl}/api/adherer/user/${userId}`);
        const payload = await response.json();

        if (!response.ok) {
          throw new Error(payload.error || "Impossible de charger l'adhesion AIDA.");
        }

        setAdhesion(payload.data || null);
      } catch (err: any) {
        setError(err.message || "Impossible de charger l'adhesion AIDA.");
      } finally {
        setLoading(false);
      }
    };

    loadAdhesion();
  }, [userId]);

  if (loading) {
    return (
      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" />
          <p className="text-muted mt-3 mb-0">Chargement des informations d'adhesion...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  if (!adhesion) {
    return (
      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="text-center py-5">
          <FaClipboardList className="text-muted mb-3" size={34} />
          <h5 className="fw-bold">Aucune adhesion AIDA trouvee</h5>
          <p className="text-muted mb-0">
            Ce profil n'a pas encore de questionnaire d'adhesion lie.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="aida-adhesion-tab">
      <style>
        {`
          .aida-adhesion-icon {
            align-items: center;
            background: #fff7df;
            border: 1px solid #ead19a;
            border-radius: 8px;
            color: #9b6b10;
            display: inline-flex;
            height: 42px;
            justify-content: center;
            min-width: 42px;
          }
        `}
      </style>

      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body>
          <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
            <div>
              <h4 className="fw-bold mb-1">Adhesion AIDA</h4>
              <p className="text-muted mb-0">
                Donnees issues du questionnaire public d'adhesion.
              </p>
            </div>
            <div className="d-flex gap-2 align-items-start">
              <Badge bg="warning" text="dark">{adhesion.account_role}</Badge>
              <Badge bg="light" text="dark" className="border">{adhesion.status}</Badge>
            </div>
          </div>

          <Row className="g-3">
            <Col md={6} xl={4}>
              <InfoCard label="Societe" value={adhesion.company_name} icon={<FaIndustry />} />
            </Col>
            <Col md={6} xl={4}>
              <InfoCard label="Site / usine" value={adhesion.site_name} icon={<FaRoute />} />
            </Col>
            <Col md={6} xl={4}>
              <InfoCard label="Wilaya" value={adhesion.wilaya} icon={<FaRoute />} />
            </Col>
            <Col md={6} xl={4}>
              <InfoCard label="Capacite theorique" value={adhesion.factory_capacity} icon={<FaIndustry />} />
            </Col>
            <Col md={6} xl={4}>
              <InfoCard label="Contact" value={adhesion.contact_name} icon={<FaClipboardList />} />
            </Col>
            <Col md={6} xl={4}>
              <InfoCard label="Email" value={adhesion.email} icon={<FaClipboardList />} />
            </Col>
            <Col md={6} xl={4}>
              <InfoCard
                label="Homologation produits"
                value={adhesion.product_homologated}
                icon={<FaCheckCircle />}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-4">
        <Col xl={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>
              <h5 className="fw-bold mb-3">Produits declares</h5>
              {adhesion.products.length ? (
                <ul className="mb-0">
                  {adhesion.products.map((product) => (
                    <li key={product.id_product_line}>{product.product_name}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted mb-0">Aucun produit declare.</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xl={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>
              <h5 className="fw-bold mb-3">
                <FaCertificate className="text-warning me-2" />
                Certifications declarees
              </h5>
              <div className="d-flex flex-wrap gap-2">
                {adhesion.certifications.length ? (
                  adhesion.certifications.map((certification) => (
                    <Badge
                      bg="success"
                      className="border"
                      key={certification.id_adhesion_certification}
                    >
                      {certification.certification_name}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted">Aucune certification declaree.</span>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12}>
          <Card className="border-0 shadow-sm rounded-4">
            <Card.Body>
              <h5 className="fw-bold mb-3">Relations constructeurs / OEM</h5>
              {adhesion.oem_contacts.length ? (
                <Table responsive hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Constructeur / OEM</th>
                      <th>Contact / fonction</th>
                      <th>Telephone</th>
                      <th>Depuis</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adhesion.oem_contacts.map((contact) => (
                      <tr key={contact.id_oem_contact}>
                        <td>{valueOrDash(contact.constructor_name)}</td>
                        <td>{valueOrDash(contact.contact_name)}</td>
                        <td>{valueOrDash(contact.phone)}</td>
                        <td>{valueOrDash(contact.duration_months)}</td>
                        <td>{valueOrDash(contact.status_level)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted mb-0">Aucune relation OEM declaree.</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xl={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>
              <h5 className="fw-bold mb-3">
                <FaExclamationTriangle className="text-warning me-2" />
                Problemes / blocages
              </h5>
              {adhesion.blockers.length ? (
                <div className="d-flex flex-column gap-2">
                  {adhesion.blockers.map((blocker) => (
                    <div className="border rounded-3 p-3" key={blocker.id_blocker}>
                      <div className="d-flex justify-content-between gap-2">
                        <p className="mb-0">{blocker.blocker_text}</p>
                        <Badge bg="danger">{blocker.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted mb-0">Aucun blocage declare.</p>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xl={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>
              <h5 className="fw-bold mb-3">
                <FaHandsHelping className="text-warning me-2" />
                Besoins d'accompagnement
              </h5>
              {adhesion.support_needs.length ? (
                <div className="d-flex flex-column gap-2">
                  {adhesion.support_needs.map((need) => (
                    <div className="border rounded-3 p-3" key={need.id_support_need}>
                      <div className="d-flex justify-content-between gap-2">
                        <p className="mb-0">{need.support_need_text}</p>
                        <Badge bg="warning" text="dark">{need.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted mb-0">Aucun besoin declare.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
